/**
 * W4b Alignment Layer — client mirror of server PRESETS
 * (server/src/config/businessPresets.ts). Ids here MUST match the server
 * vocabulary (guarded there by businessPresets.test.ts): hub tab ids are
 * BusinessHub SYSTEM_TABS ids; trigger categories are the picker optgroup
 * keys; pack ids are the installer catalog ids.
 *
 * PRESENTATION-ONLY lens: reorder/relabel/pin — never remove capabilities.
 */
import type { BusinessTypeId } from './businessTypeStore';

/**
 * "Which kind is this workspace" is a resolution with a lifecycle, not a table
 * lookup, and it lives in ./businessTypeStore. Re-exported here because fifteen
 * components import `useBusinessType` from this module and none of them should
 * have to care that it moved.
 */
export {
  BUSINESS_TYPE_IDS,
  READ_DEADLINE_MS,
  useBusinessType,
  useWorkspaceName,
  retryBusinessType,
  invalidateBusinessType,
} from './businessTypeStore';
export type {
  BusinessTypeId,
  BusinessTypeSource,
  BusinessTypeStatus,
  BusinessTypeState,
} from './businessTypeStore';

// Kit KIND_META labels (short forms) — ONE vocabulary. Step0Kind, the
// Account section and the funnel kind badge all read this; two disagreeing
// label sets meant the same workspace was named differently per surface.
export const BUSINESS_TYPE_LABELS: Record<BusinessTypeId, string> = {
  ecommerce: 'E-commerce',
  service: 'Services',
  course: 'Courses',
  saas: 'SaaS',
  coaching: 'Coaching',
  content_creator: 'Content creator',
};

export interface PresetHubTab {
  id: 'overview' | 'contacts' | 'inventory' | 'orders' | 'ads' | 'finance';
  label?: string;
}

export interface ClientBusinessPreset {
  hubTabs: PresetHubTab[];
  /** Trigger-picker optgroup keys in display order (order-only, all always render). */
  triggerCategories: string[];
  /** Phase G vertical terminology — mirror of server PRESETS nouns. */
  nouns: { person: string; transaction: string; catalog: string };
  /**
   * Kit KIND_META.accent — the kind's marker colour (kind tiles, the funnel
   * kind badge). Kit values kept where allowed; the kit's Services accent is
   * the banned violet-600, substituted with indigo-500 per the repo's
   * standing violet-substitution precedent (winback, popup_form, boards'
   * DEFAULT_GROUP_COLOR). coaching/content_creator extend beyond the kit's
   * four kinds.
   */
  accent: string;
}

const ALL_CATEGORIES = [
  'subscribers', 'browse_cart', 'orders', 'lifecycle', 'leads_bookings',
  'community', 'email_engagement', 'time_based', 'custom',
];

function orderedCategories(first: string[]): string[] {
  return [...first, ...ALL_CATEGORIES.filter((c) => !first.includes(c))];
}

export const CLIENT_PRESETS: Record<BusinessTypeId, ClientBusinessPreset> = {
  ecommerce: {
    accent: '#16a34a',
    hubTabs: [
      { id: 'overview' }, { id: 'contacts' }, { id: 'inventory' },
      { id: 'orders' }, { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: [...ALL_CATEGORIES],
    nouns: { person: 'Customer', transaction: 'Order', catalog: 'Inventory' },
  },
  service: {
    accent: '#6366f1',
    hubTabs: [
      { id: 'overview' }, { id: 'contacts', label: 'Clients' },
      // Kind-alignment §5/§8: inventory renders the service catalog
      // (label = nouns.catalog); ads is real for every kind.
      { id: 'inventory', label: 'Services' },
      { id: 'orders', label: 'Bookings' }, { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: orderedCategories(['leads_bookings', 'lifecycle', 'community']),
    nouns: { person: 'Client', transaction: 'Booking', catalog: 'Services' },
  },
  course: {
    accent: '#d97706',
    hubTabs: [
      { id: 'overview' }, { id: 'contacts', label: 'Students' },
      // Kind-alignment §8: ads is real for every kind.
      { id: 'orders', label: 'Enrollments' }, { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: orderedCategories(['leads_bookings', 'community', 'lifecycle']),
    nouns: { person: 'Student', transaction: 'Enrollment', catalog: 'Cohorts' },
  },
  saas: {
    accent: '#0891b2',
    // No `orders` slot ("Subscriptions") — the one deliberate difference from
    // the server preset. That slot renders OrdersTab, the store's Order table,
    // and a saas business's subscriptions are MerchantSubscription rows the
    // Stripe webhook writes and no list reads: six live subscriptions showed
    // "No subscriptions yet" (verified 2026-10-10). Hidden until a real
    // subscriptions list exists; MRR, trials, cancellations and active
    // subscriptions stay on the Overview. A connected store still gets its
    // Orders tab (withStoreTabs) and ?tab=orders still resolves. The server
    // entry keeps `orders`, because there it declares the system boards.
    hubTabs: [
      { id: 'overview' }, { id: 'contacts', label: 'Subscribers' },
      { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: orderedCategories(['lifecycle', 'leads_bookings', 'email_engagement']),
    nouns: { person: 'Subscriber', transaction: 'Subscription', catalog: 'Plans' },
  },
  coaching: {
    accent: '#0d9488',
    hubTabs: [
      { id: 'overview' }, { id: 'contacts', label: 'Clients' },
      // Kind-alignment §5/§8: catalog surface + ads for every kind.
      { id: 'inventory', label: 'Programs' },
      { id: 'orders', label: 'Sessions' }, { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: orderedCategories(['leads_bookings', 'community', 'lifecycle']),
    nouns: { person: 'Client', transaction: 'Session', catalog: 'Programs' },
  },
  content_creator: {
    accent: '#db2777',
    hubTabs: [
      { id: 'overview' }, { id: 'contacts', label: 'Audience' },
      { id: 'orders', label: 'Deals' }, { id: 'ads' }, { id: 'finance' },
    ],
    triggerCategories: orderedCategories(['community', 'leads_bookings', 'email_engagement']),
    nouns: { person: 'Follower', transaction: 'Deal', catalog: 'Content' },
  },
};

export function getClientPreset(businessType: string | null | undefined): ClientBusinessPreset {
  return CLIENT_PRESETS[(businessType as BusinessTypeId) ?? 'ecommerce'] ?? CLIENT_PRESETS.ecommerce;
}
